import type { ILookupOption } from '@types';

import { Combobox, Icon } from '@salesforce/design-system-react';
import { IconCategory } from '@types';
import { useEffect, useRef, useState } from 'react';

import { SearchModal, type SearchModalConfig } from './SearchModal';

const SEARCH_DEBOUNCE_MS = 300;
const MIN_SEARCH_LENGTH = 2;
const OPEN_SEARCH_MODAL_OPTION_ID = 'open-search-modal';

interface RecordLookupProps<TRecord extends { Id: string }, TField extends string> {
  disabled?: boolean;
  iconCategory: IconCategory;
  iconName: string;
  label: string;
  modal: SearchModalConfig<TRecord, TField>;
  onChange: (value: ILookupOption | undefined) => void;
  placeholder?: string;
  search: (term: string) => Promise<ILookupOption[]>;
  value: ILookupOption | undefined;
}

/** A single-select Salesforce entity lookup, built on the SLDS `Combobox` inline-listbox variant. */
export const RecordLookup = <TRecord extends { Id: string }, TField extends string>({
  disabled,
  iconCategory,
  iconName,
  label,
  modal,
  onChange,
  placeholder,
  search,
  value,
}: RecordLookupProps<TRecord, TField>) => {
  const [inputValue, setInputValue] = useState('');
  const [options, setOptions] = useState<ILookupOption[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [errorText, setErrorText] = useState<string>();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  // Guards against a slower, earlier request overwriting a later one's results.
  const requestIdRef = useRef(0);

  useEffect(
    () => () => {
      clearTimeout(debounceRef.current);
    },
    [],
  );

  const toIcon = () => <Icon category={iconCategory} name={iconName} size="small" />;

  const resetSearch = () => {
    clearTimeout(debounceRef.current);
    requestIdRef.current += 1;
    setInputValue('');
    setOptions([]);
    setIsSearching(false);
    setErrorText(undefined);
  };

  const clearSelection = () => {
    resetSearch();
    onChange(undefined);
  };

  const runSearch = (term: string) => {
    clearTimeout(debounceRef.current);
    requestIdRef.current += 1;
    console.debug(`[RecordLookup:${label}] input changed`, { term });
    if (term.length < MIN_SEARCH_LENGTH) {
      setOptions([]);
      setIsSearching(false);
      setErrorText(undefined);
      return;
    }
    debounceRef.current = setTimeout(() => {
      const requestId = requestIdRef.current;
      void (async () => {
        setIsSearching(true);
        setErrorText(undefined);
        console.debug(`[RecordLookup:${label}] debounce fired, searching`, { requestId, term });
        try {
          const results = await search(term);
          console.debug(`[RecordLookup:${label}] search resolved`, { requestId, resultCount: results.length });
          if (requestId !== requestIdRef.current) return;
          setOptions(results);
        } catch (error) {
          console.debug(`[RecordLookup:${label}] search rejected`, { error, requestId });
          if (requestId !== requestIdRef.current) return;
          setErrorText(error instanceof Error ? error.message : 'Search failed');
          setOptions([]);
        } finally {
          if (requestId === requestIdRef.current) setIsSearching(false);
        }
      })();
    }, SEARCH_DEBOUNCE_MS);
  };

  return (
    <>
      <Combobox
        disabled={disabled}
        errorText={errorText}
        events={{
          onChange: (_event, data) => {
            setInputValue(data.value);
            runSearch(data.value);
          },
          onRequestRemoveSelectedOption: () => {
            clearSelection();
          },
          onSelect: (_event, data) => {
            if (data.selection[0]?.id === OPEN_SEARCH_MODAL_OPTION_ID) {
              setIsModalOpen(true);
              return;
            }
            const selected = options.find(option => option.id === data.selection[0]?.id);
            resetSearch();
            onChange(selected);
          },
        }}
        hasInputSpinner={isSearching}
        id={`record-lookup-${label}`}
        labels={{ label, placeholder: placeholder ?? `Search ${label}…` }}
        menuPosition="relative"
        options={[
          {
            icon: <Icon category={IconCategory.Utility} name="search" size="x-small" />,
            id: OPEN_SEARCH_MODAL_OPTION_ID,
            label: 'Open Search Modal',
          },
          ...options.map(option => ({ ...option, icon: toIcon() })),
        ]}
        selection={value ? [{ ...value, icon: toIcon() }] : []}
        singleInputDisabled={disabled}
        value={inputValue}
        variant="inline-listbox"
      />
      {isModalOpen ? (
        <SearchModal
          {...modal}
          isOpen
          label={label}
          onRequestClose={() => {
            setIsModalOpen(false);
          }}
          onSelect={selected => {
            resetSearch();
            onChange(selected);
          }}
        />
      ) : null}
    </>
  );
};
